/**
 * Support chat API client. Wraps the six public widget endpoints under
 * /api/casino/support.
 *
 *   GET  /config          -> { widget_enabled }   (render nothing unless true)
 *   POST /chat/session    -> create a conversation (guest or logged-in player)
 *   POST /chat/message    -> THIN: persist the user turn + enqueue the bot turn
 *   GET  /chat/messages    -> poll for the filtered reply
 *   POST /chat/escalate   -> "talk to a human" -> support ticket
 *   POST /chat/close      -> the player ends the chat
 *
 * DELIBERATELY a tiny fetch wrapper rather than the house axios apiClient. That
 * client retries 5xx up to three times (a retried POST /chat/message would
 * double-post the player's turn), force-signs-out on some 401 bodies, and fires
 * global toasts. None of that is right for an optional, auth-optional support
 * surface that must degrade quietly.
 *
 * Auth is OPTIONAL and resolved per call. A logged-in player's Supabase bearer
 * goes in Authorization; a guest instead presents the one-time session token
 * from /chat/session in the x-support-session header. The server is always the
 * authority on ownership.
 *
 * Field names on the wire are snake_case (conversation_id, origin_url) to match
 * the backend Zod schemas.
 */

import authService from "../../services/authService";

/** Same-origin in dev (vite proxies /api to the backend) and in production. */
const API_BASE = import.meta.env.VITE_API_BASE_URL || "";
const SUPPORT_BASE = `${API_BASE}/api/casino/support`;

/** Header a guest presents to prove ownership of a guest conversation. */
const SESSION_HEADER = "x-support-session";

/** Error thrown on a non-2xx support response. Carries status + backend code. */
export class SupportApiError extends Error {
  constructor(status, message, code) {
    super(message);
    this.name = "SupportApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * Resolve the current player's Supabase access token, or null for a guest.
 * authService.getSession() reads an in-memory session first and caps its slow
 * path at 800ms, so this is cheap enough to call on every poll tick.
 */
export async function resolveAccessToken() {
  try {
    const { session } = await authService.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Pull a human-readable message out of an error body. The engine's 429 bodies
 * carry { code, message } and the widget shows that message verbatim; the
 * route-level throttle instead sends { error }.
 */
function messageFromBody(body, status) {
  if (body && typeof body === "object") {
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  return `Request failed with status ${status}`;
}

async function supportFetch(path, options = {}) {
  const { method = "GET", body, auth = {}, signal } = options;

  const headers = {};
  if (body != null) headers["Content-Type"] = "application/json";
  if (auth.accessToken) headers.Authorization = `Bearer ${auth.accessToken}`;
  if (auth.sessionToken) headers[SESSION_HEADER] = auth.sessionToken;

  const response = await fetch(`${SUPPORT_BASE}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    signal,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new SupportApiError(
      response.status,
      messageFromBody(payload, response.status),
      payload && typeof payload.code === "string" ? payload.code : undefined,
    );
  }

  // sendDirect spreads the handler's result into a { success, status,
  // timestamp, ...result } envelope, so the payload IS the result.
  return payload || {};
}

/**
 * Public widget-visibility flag. The only call made before the player interacts.
 * Fails dark: any error means "keep the previous support surface".
 */
export async function fetchSupportConfig(signal) {
  const res = await supportFetch("/config", { method: "GET", signal });
  return { widgetEnabled: res.widget_enabled === true };
}

/** Create a conversation. Called lazily on the player's FIRST message. */
export async function createSupportSession(input) {
  return supportFetch("/chat/session", {
    method: "POST",
    body: {
      channel: input.channel,
      ...(input.originUrl ? { origin_url: input.originUrl } : {}),
      ...(input.email ? { email: input.email } : {}),
    },
    auth: { accessToken: input.accessToken },
  });
}

/** THIN: persist the player's turn and enqueue the reply. Poll for it after. */
export async function postSupportMessage(input) {
  return supportFetch("/chat/message", {
    method: "POST",
    body: { conversation_id: input.conversationId, body: input.body },
    auth: { accessToken: input.accessToken, sessionToken: input.sessionToken },
  });
}

/** Poll for messages after a cursor. Bodies are always the filtered text. */
export async function pollSupportMessages(input) {
  const params = new URLSearchParams({ conversation_id: input.conversationId });
  if (input.after) params.set("after", input.after);
  return supportFetch(`/chat/messages?${params.toString()}`, {
    method: "GET",
    auth: { accessToken: input.accessToken, sessionToken: input.sessionToken },
    signal: input.signal,
  });
}

/** "Talk to a person". Files a real support ticket for this conversation. */
export async function escalateSupport(input) {
  return supportFetch("/chat/escalate", {
    method: "POST",
    body: {
      conversation_id: input.conversationId,
      ...(input.reason ? { reason: input.reason } : {}),
    },
    auth: { accessToken: input.accessToken, sessionToken: input.sessionToken },
  });
}

/**
 * End the chat: mark the conversation closed server-side. Best effort by
 * contract, callers reset the widget locally whether or not this lands.
 */
export async function closeSupportConversation(input) {
  return supportFetch("/chat/close", {
    method: "POST",
    body: { conversation_id: input.conversationId },
    auth: { accessToken: input.accessToken, sessionToken: input.sessionToken },
  });
}
